// ── Shared constants and helpers for Analyzer components ─────────────────────

import { canonCapBuckets } from './capBuckets';

export const C = {
  pageBg:    '#F1F5F9',
  cardBg:    '#FFFFFF',
  border:    '#E2E8F0',
  borderMd:  '#CBD5E1',
  text:      '#0F172A',
  textMd:    '#475569',
  textSm:    '#94A3B8',
  primary:   '#2563EB',
  primaryLt: '#EFF6FF',
  success:   '#16A34A',
  danger:    '#DC2626',
  warning:   '#D97706',
  shadow:    '0 1px 3px rgba(0,0,0,0.08)',
  shadowMd:  '0 4px 12px rgba(0,0,0,0.10)',
};

export const FILTER_TYPES = [
  { value: 'id',           label: 'ID',           type: 'id_search' },
  { value: 'result_date',  label: 'Results date', type: 'daterange' },
  { value: 'start_period', label: 'Start period', type: 'daterange' },
  { value: 'end_period',   label: 'End period',   type: 'daterange' },
  { value: 'monthly_pct',  label: 'Monthly %',    type: 'numrange'  },
  { value: 'yearly_pct',   label: 'Yearly %',     type: 'numrange'  },
  // Avg. Yr% — та сама величина, що в колонці: середня річна по врахованих
  // періодах, без періодів — річна за все вікно.
  { value: 'avg_yearly',   label: 'Avg. Yr %',    type: 'numrange'  },
  // Кількість врахованих періодів (колонка Periods) — щоб відділити рядки
  // старих сіток зі 100 вікнами від нових із 1074.
  { value: 'periods',      label: 'Periods',      type: 'numrange', unit: '' },
  { value: 'return_pct',   label: 'Return %',     type: 'numrange'  },
  { value: 'win_rate',     label: 'Win %',        type: 'numrange'  },
  { value: 'sharpe',       label: 'Sharpe',       type: 'numrange', unit: '' },
  { value: 'max_dd',       label: 'MaxDD %',      type: 'numrange'  },
  { value: 'prof_months',  label: 'Profit.Mo %',  type: 'numrange'  },
  { value: 'worst_month',  label: 'Worst Mo %',   type: 'numrange'  },
  { value: 'calmar',       label: 'Calmar',       type: 'numrange', unit: '' },
  { value: 'status',       label: 'Status',       type: 'select',
    options: ['completed', 'running', 'failed', 'pending'] },
];

export function formatFilterLabel(f) {
  if (f.datePreset) return f.datePreset;
  if (f.type === 'daterange') {
    const parts = [];
    if (f.from) parts.push(`≥${f.from}`);
    if (f.to)   parts.push(`≤${f.to}`);
    return parts.join(' ') || '—';
  }
  if (f.type === 'numrange') {
    const unit = FILTER_TYPES.find(t => t.value === f.field)?.unit ?? '%';
    const parts = [];
    if (f.from != null) parts.push(`≥${f.from}${unit}`);
    if (f.to   != null) parts.push(`≤${f.to}${unit}`);
    return parts.join(' ') || '—';
  }
  if (f.type === 'select')    return f.value || '—';
  if (f.type === 'id_search') return f.value || '—';
  return '';
}

export const COLUMN_GROUPS = [
  { label: 'Strategy params', cols: [
    ['buy_pct','Buy%'],['t_buy','T-Buy'],['drop_pct','Drop%'],['t_sell','T-Sell'],
    ['n_cos','N cos.'],['pos_pct','Pos%'],['capital','Capital'],
  ]},
  // Headline result columns. When a row has periods, each shows the across-periods
  // aggregate (see TableRow `eff`): Win%=weighted, Monthly/Yearly=avg, Sharpe=pooled,
  // MaxDD/Calmar/Worst Mo%=worst, Profit.Mo%=pooled. Non-aggregatable per-row metrics
  // (Total%, Final$, Buys, Sells, costs) live only in the periods popup now.
  { label: 'Results (aggregate)', cols: [
    ['win_rate','Win%'],
    ['monthly','Monthly%'],['yearly','Yearly%'],
    ['worst_yearly_periods','Worst Yr%'],
    ['neg_yearly_share_periods','% of worst Yr'],
    ['neg_yearly_avg_periods','Average % in worst Yr'],
    ['best_yearly_periods','Best Yr%'],
    ['pos_yearly_share_periods','% of no worst Yr'],
    ['pos_yearly_avg_periods','Average % in no worst Yr'],
    ['best_worst_yearly_periods','Best-worst Yr%'],
    ['avg_yearly_periods','Avg. Yr%'],
    ['avg_deals_periods','Avg. Deals in Yr'],
    ['sharpe','Sharpe'],['maxdd','MaxDD'],
    ['periods_count','Periods'],
  ]},
  { label: 'Consistency', cols: [
    ['prof_months','Profit.Mo%'],['worst_month','Worst Mo%'],
    ['calmar','Calmar'],
  ]},
  { label: 'Risk Protection', cols: [
    ['overnight','Overnight'],['weekend','Weekend'],['max_loss','Max Loss%'],
  ]},
  { label: 'Advanced', cols: [
    ['sell_mode','Sell Logic'],['stagnation','Stagnation'],['intraday','Intraday'],
    ['reinvest','Reinvest'],['hard_stop','Hard Stop'],['peak_window','Peak Window'],
    ['buy_cap','Buy Cap'],['overpriced','Overpriced'],
    // Dip і Take Profit розділені на число й вікно (запит менеджера 26.08):
    // склеєні «45%/50b» сортувались лише як одне ціле, а шукати треба окремо
    // за глибиною падіння і окремо за довжиною вікна.
    ['dip_pct','Dip %'],['dip_bars','Dip bars'],
    ['rotation','Rotation'],['opportunity','Opportunity'],['hold_to_cost','Hold to Cost'],['htc_cost','HTC breakeven +%'],
    // Продаж під покупку (менеджер 15.10): [2c], [2d] і галочка блоку [3].
    ['swap_defer','Sell only for Buy'],['swap_tp_frac','TP Fraction %'],['swap_htc','HTC → Buy'],['no_growth','Buy% exit'],['smart_gen','Smart gen'],
    ['tp_pct','Take Profit %'],['tp_bars','Take Profit bars'],
    ['tp_sell_immediately','TP: Sell immed.'],
  ]},
  // Шари реалізму — блоки 5-10 форми пошуку. Прочерк у клітинці = шар був
  // вимкнений, тобто рядок рахований старими правилами і з новими не
  // порівнюється напряму.
  { label: 'Realism (blocks 5-10)', cols: [
    ['liquidity','Liquidity'],['impact','Impact'],['margin','Leverage'],
    ['pit_universe','Hist. Cap'],['delisting','Delisting'],['reorg','Issuer Chg'],
    ['per_bar_universe','Univ./bar'],
    ['slicing','Slicing'],['partial_sells','Partial Sell'],
    ['dynamic_n','Dynamic N'],
    // Дві осі менеджера 2026-08-19. Обидві — ВІСЬ ПЕРЕБОРУ, тож без цих колонок
    // рядки одного пошуку виглядають однаковими, хоч рахувались по-різному.
    ['buy_priority','Buy Priority'],['cap_set','Cap Set'],
  ]},
  { label: 'Context', cols: [
    ['regime','Regime'],['tickers','Tickers'],['sector','Sector'],
    ['market_cap','Cap'],['country','Country'],
  ]},
];

export const DEFAULT_COLUMNS = {
  buy_pct: true, t_buy: true, drop_pct: true, t_sell: true,
  n_cos: true, pos_pct: true, capital: true,
  // Headline metrics show the across-periods aggregate when a row has periods.
  // Total%, Final$, Buys, Sells, Commission, Spread and within-period smoothness
  // metrics (R²/Stability/Downside/Upside/Mo.Std) were removed from the main table —
  // the first group lives per-period in the popup, the smoothness set was dropped
  // (it measures within-period smoothness, not cross-period robustness).
  win_rate: true,
  monthly: true, yearly: true, sharpe: true, maxdd: true,
  // "Worst Yr%" stays ON (headline Yearly% shows the AVG year; this is the unique
  // worst-year value across periods). Toggleable via ColumnToggle.
  // Best Yr% + Best-worst Yr% (spread) + Avg. Yr% form a grouped block for spotting
  // single-year-carried strategies: sort by Best-worst asc (low spread = consistent)
  // or Avg. Yr% desc, then scan.
  worst_yearly_periods: true,
  // Частки вікон за знаком (запит менеджера 07.09): одне збиткове вікно зі ста
  // і половина збиткових дають ОДНАКОВИЙ Worst Yr%, а це різні стратегії.
  neg_yearly_share_periods: true,
  neg_yearly_avg_periods: true,
  best_yearly_periods: true,
  pos_yearly_share_periods: true,
  pos_yearly_avg_periods: true,
  best_worst_yearly_periods: true,
  avg_yearly_periods: true,
  avg_deals_periods: true,
  periods_count: true,
  prof_months: true, worst_month: true, calmar: true,
  overnight: true, weekend: true, max_loss: true,
  sell_mode: false, stagnation: false, intraday: false, reinvest: true, hard_stop: false, peak_window: false,
  buy_cap: false, overpriced: false, rotation: false, opportunity: false, hold_to_cost: false, htc_cost: false,
  swap_defer: false, swap_tp_frac: false, swap_htc: false, no_growth: false, smart_gen: false,
  // Dip / Take Profit — по дві колонки на кожен (значення + вікно), щоб кожну
  // можна було сортувати окремо.
  dip_pct: false, dip_bars: false, tp_pct: false, tp_bars: false, tp_sell_immediately: false,
  // Шари реалізму: за замовчуванням приховані (таблиця і так широка), але
  // доступні в «Колонки» — інакше з двох сусідніх рядків не видно, який із них
  // рахований з обігом/делістингами, а який без.
  liquidity: false, impact: false, margin: false, pit_universe: false,
  delisting: false, reorg: false, per_bar_universe: false, slicing: false,
  partial_sells: false, dynamic_n: false,
  buy_priority: false, cap_set: false,
  regime: true, tickers: true, sector: false, market_cap: false, country: false,
};

export const PAGE_SIZE_OPTS = [25, 50, 100, 250, 500, 1000, 'all'];
export const DEFAULT_PAGE_SIZE = 100;

// ── Formatting helpers ────────────────────────────────────────────────────────
export function fmtNum(n, dec = 2) {
  return n != null ? Number(n).toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec }) : '—';
}
export function fmtUsd(n) { return n != null && n !== 0 ? '$' + fmtNum(n, 0) : '—'; }
export function fmtPct(n) { return n != null ? (n >= 0 ? '+' : '') + fmtNum(n) + '%' : '—'; }
export function fmtAnnualPct(n, dec = 1) {
  if (n == null || !Number.isFinite(n)) return '—';
  const sign = n >= 0 ? '+' : '';
  if (Math.abs(n) >= 99999) return sign + Math.round(n / 1000) + 'k%';
  if (Math.abs(n) >= 9999)  return sign + Math.round(n) + '%';
  return sign + Number(n).toFixed(dec) + '%';
}

// ── Cell renderers (metadata) ─────────────────────────────────────────────────
export function renderRegime(meta) {
  const f = meta?.regime_filter || [];
  if (!f.length) return <span style={{ fontSize: 12, color: '#94a3b8' }}>🌐</span>;
  return (
    <span style={{ fontSize: 13 }}>
      {f.map(r => r === 'bull' ? '🟢' : r === 'bear' ? '🔴' : r === 'sideways' ? '🟡' : '').join('')}
    </span>
  );
}

// Набір капіталізації — вісь перебору (менеджер 2026-08-19): в одному пошуку
// рядки мають РІЗНІ набори, а мета батчу несе лише перший набір форми. Тому
// колонки Tickers/Market Cap беруть набір із параметрів рядка, якщо він там є.
// Без цього всі рядки сітки з кількома наборами показували перший набір.
export function rowMeta(meta, params) {
  const b = params?.market_cap_buckets;
  if (!Array.isArray(b) || !b.length) return meta;
  const names = canonCapBuckets(b);
  const custom = b.some(x => String(x).trim().toLowerCase() === 'custom');
  let type;
  if (!names.length) type = custom ? 'custom' : 'All';
  else type = custom ? [...names, 'custom'].join('+') : names.join('+');
  if (meta?.market_cap_type === type) return meta;
  return { ...(meta || {}), market_cap_type: type, market_cap_buckets: b };
}

export function renderTickers(meta) {
  if (meta?.ticker_mode === 'specific') {
    const t = meta.specific_tickers || [];
    if (t.length <= 3) return <span style={{ fontSize: 11, fontFamily: 'monospace', color: '#2563eb' }}>🎯 {t.join(', ')}</span>;
    return <span title={t.join(', ')} style={{ fontSize: 11, color: '#2563eb', cursor: 'help' }}>🎯 {t.slice(0, 2).join(', ')} +{t.length - 2}</span>;
  }
  const parts = [];
  if (meta?.countries?.length === 1) parts.push(meta.countries[0]);
  if (meta?.sectors?.length) parts.push(meta.sectors.join('/'));
  if (meta?.market_cap_type && meta.market_cap_type !== 'All') parts.push(meta.market_cap_type);
  return <span style={{ fontSize: 11, color: '#64748b' }}>🌐 {parts.join(' · ') || 'All'}</span>;
}

export function renderSector(meta) {
  if (meta?.ticker_mode === 'specific') return <span style={{ fontSize: 11, color: '#94a3b8' }}>—</span>;
  const s = meta?.sectors || [];
  const ind = meta?.industries || [];
  if (!s.length) return <span style={{ fontSize: 11, color: '#94a3b8' }}>All</span>;
  const label = s.length === 1 ? s[0] : `${s.length} sectors`;
  const iLabel = ind.length ? ` › ${ind.length === 1 ? ind[0] : ind.length + ' ind.'}` : '';
  return <span title={[...s, ...ind.map(x => '  · ' + x)].join('\n')} style={{ fontSize: 11, cursor: ind.length ? 'help' : 'default' }}>{label}{iLabel}</span>;
}

const CAP_COLORS = { Mega: '#7c3aed', Large: '#2563eb', Mid: '#059669', Small: '#d97706' };

export function renderMarketCap(meta) {
  if (meta?.ticker_mode === 'specific') return <span style={{ fontSize: 11, color: '#94a3b8' }}>—</span>;
  const type = meta?.market_cap_type;
  if (!type || type === 'All') return <span style={{ fontSize: 11, color: '#94a3b8' }}>All</span>;
  const fmtCap = v => v >= 1e9 ? `${(v / 1e9).toFixed(0)}B` : `${(v / 1e6).toFixed(0)}M`;
  const rangeStr = () => {
    const minStr = meta.market_cap_min ? fmtCap(meta.market_cap_min) : '0';
    const maxStr = (!meta.market_cap_max || meta.market_cap_max >= 1e14) ? '∞' : fmtCap(meta.market_cap_max);
    return `${minStr}–${maxStr}`;
  };
  if (type === 'custom') return <span style={{ fontSize: 10 }}>{rangeStr()}</span>;
  // Several buckets at once → "Mid+Small" (label built by capTypeLabel in StrategyAnalyzer).
  const parts = String(type).split('+');
  if (parts.length > 1) {
    const title = parts.map(x => x === 'custom' ? `custom ${rangeStr()}` : x).join(' + ');
    return (
      <span title={title} style={{ fontSize: 10, fontWeight: 600, cursor: 'help' }}>
        {parts.map((x, i) => (
          <span key={x}>
            {i > 0 && <span style={{ color: '#94a3b8' }}>+</span>}
            <span style={{ color: CAP_COLORS[x] || '#334155' }}>{x === 'custom' ? '⚙' : x}</span>
          </span>
        ))}
      </span>
    );
  }
  return <span style={{ fontSize: 11, fontWeight: 600, color: CAP_COLORS[type] || '#334155' }}>{type}</span>;
}

export function renderCountry(meta) {
  if (meta?.ticker_mode === 'specific') return <span style={{ fontSize: 11, color: '#94a3b8' }}>—</span>;
  const c = meta?.countries || [];
  if (!c.length) return <span style={{ fontSize: 11, color: '#94a3b8' }}>—</span>;
  return <span style={{ fontSize: 11 }}>{c.join(', ')}</span>;
}

export function metaTooltip(meta) {
  if (!meta) return '';
  const lines = [];
  const regime = meta.regime_filter || [];
  lines.push(`Regime: ${!regime.length ? 'All' : regime.map(r => r === 'bull' ? '🟢 Bull' : r === 'bear' ? '🔴 Bear' : '🟡 Sideways').join(', ')}`);
  if (meta.ticker_mode === 'specific') {
    lines.push(`Tickers: ${(meta.specific_tickers || []).join(', ')}`);
  } else {
    if (meta.countries?.length)   lines.push(`Country: ${meta.countries.join(', ')}`);
    if (meta.sectors?.length)     lines.push(`Sector: ${meta.sectors.join(', ')}`);
    if (meta.industries?.length)  lines.push(`Industry: ${meta.industries.join(', ')}`);
    if (meta.indexes?.length)     lines.push(`Index: ${meta.indexes.join(' + ')}`);
    if (meta.market_cap_type && meta.market_cap_type !== 'All') {
      const fmt = v => v >= 1e9 ? `${(v / 1e9).toFixed(0)}B` : `${(v / 1e6).toFixed(0)}M`;
      const range = () => {
        const capMax = (!meta.market_cap_max || meta.market_cap_max >= 1e14) ? '∞' : fmt(meta.market_cap_max);
        return `${fmt(meta.market_cap_min || 0)}–${capMax}`;
      };
      if (meta.market_cap_type === 'custom') {
        lines.push(`Cap: ${range()}`);
      } else {
        // Multi-bucket labels look like "Mid+Small" / "Mega+custom".
        lines.push(`Cap: ${String(meta.market_cap_type).split('+')
          .map(x => x === 'custom' ? `custom ${range()}` : x).join(' + ')}`);
      }
    }
    if (meta.beta_min || meta.beta_max) lines.push(`Beta: ${meta.beta_min || 0}–${meta.beta_max || '∞'}`);
    if (meta.min_avg_volume) lines.push(`Min Vol: ${(meta.min_avg_volume / 1000).toFixed(0)}K`);
  }
  return lines.join('\n');
}

// ── Common cell styles ────────────────────────────────────────────────────────
export const td = {
  padding: '8px 10px',
  border: `1px solid #E2E8F0`,
  color: '#0F172A',
  whiteSpace: 'nowrap',
  verticalAlign: 'middle',
};

export const th = {
  padding: '9px 10px',
  background: '#F8FAFC',
  border: `1px solid #E2E8F0`,
  color: '#475569',
  fontWeight: 600,
  textAlign: 'left',
  whiteSpace: 'nowrap',
  position: 'sticky',
  top: 0,
  zIndex: 3,
  cursor: 'pointer',
  userSelect: 'none',
};

export const btnSm = {
  padding: '5px 10px',
  background: 'transparent',
  color: C.primary,
  border: `1px solid #E2E8F0`,
  borderRadius: 6,
  fontSize: 12,
  cursor: 'pointer',
};

export const inputStyle = {
  padding: '7px 9px',
  border: `1px solid #E2E8F0`,
  borderRadius: 7,
  fontSize: 13,
  color: '#0F172A',
  background: '#fff',
  boxSizing: 'border-box',
  outline: 'none',
};
